import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../App";
import { useTranslation } from "react-i18next";
import QMSLayout from "../components/QMSLayout";
import {
  CreditCard, Receipt, QrCode, CalendarClock, Webhook, BarChart3,
  ShieldCheck, AlertCircle, RefreshCw, FileDown, Search,
  Plus, X, ChevronRight, Loader2, CheckCircle2, XCircle, Ban
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { id: "overview", label: "Overview", icon: BarChart3 },
  { id: "boleto", label: "Boleto", icon: Receipt },
  { id: "pix", label: "PIX", icon: QrCode },
  { id: "checkout", label: "Checkout", icon: CreditCard },
  { id: "schedule", label: "Scheduled", icon: CalendarClock },
  { id: "webhooks", label: "Webhooks", icon: Webhook },
  { id: "statements", label: "Statements", icon: BarChart3 },
];

const StatusBadge = ({ status }) => {
  const map = {
    active: { color: "bg-emerald-500/20 text-emerald-300", icon: CheckCircle2 },
    paid: { color: "bg-emerald-500/20 text-emerald-300", icon: CheckCircle2 },
    pending: { color: "bg-amber-500/20 text-amber-300", icon: Loader2 },
    cancelled: { color: "bg-red-500/20 text-red-300", icon: XCircle },
    canceled: { color: "bg-red-500/20 text-red-300", icon: XCircle },
    error: { color: "bg-red-500/20 text-red-300", icon: AlertCircle },
    disabled: { color: "bg-zinc-500/20 text-zinc-400", icon: Ban },
  };
  const s = map[(status || "").toLowerCase()] || map.pending;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${s.color}`} data-testid={`status-badge-${status}`}>
      <Icon size={12} /> {status || "unknown"}
    </span>
  );
};

async function apiFetch(path, opts = {}) {
  const res = await fetch(`${API}${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...opts.headers },
    ...opts,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(err.detail || `Error ${res.status}`);
  }
  return res.json();
}

// ─── OVERVIEW TAB ──────────────────────────────────────
function OverviewTab({ status }) {
  const enabled = status?.enabled;
  const modules = status?.api_modules || [];
  const profiles = status?.identity_profiles?.profiles || {};
  const brProfile = profiles?.BR || {};
  const globalProfile = profiles?.DEFAULT || {};

  return (
    <div className="space-y-6" data-testid="payments-overview-tab">
      {/* Status Card */}
      <div className="p-6 rounded-xl border border-zinc-700/60 bg-zinc-800/50">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-zinc-100">C6Bank Integration Status</h3>
          <StatusBadge status={enabled ? "active" : "disabled"} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div>
            <span className="text-zinc-400">Environment</span>
            <p className="text-zinc-100 font-medium mt-1">{status?.environment || "—"}</p>
          </div>
          <div>
            <span className="text-zinc-400">Credentials</span>
            <p className="text-zinc-100 font-medium mt-1">{status?.has_credentials ? "Configured" : "Not configured"}</p>
          </div>
          <div>
            <span className="text-zinc-400">API Modules</span>
            <p className="text-zinc-100 font-medium mt-1">{modules.length} active</p>
          </div>
        </div>
      </div>

      {/* Financial Identity Profiles */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Brazil Profile */}
        <div className="p-6 rounded-xl border border-cyan-700/40 bg-zinc-800/50" data-testid="identity-profile-br">
          <div className="flex items-center gap-2 mb-3">
            <ShieldCheck size={16} className="text-cyan-400" />
            <h3 className="text-base font-semibold text-zinc-100">Brazil Financial Profile</h3>
            <span className="ml-auto px-2 py-0.5 text-[10px] font-semibold rounded bg-cyan-600/20 text-cyan-300">BR</span>
          </div>
          <p className="text-xs text-zinc-400 mb-3">Applied to Boleto, PIX, Checkout, and other BR-facing financial documents.</p>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-zinc-400">Company</span><span className="text-zinc-100">{brProfile.company_name || "—"}</span></div>
            <div className="flex justify-between"><span className="text-zinc-400">CNPJ</span><span className="text-zinc-100 font-mono">{brProfile.cnpj || "—"}</span></div>
            <div className="flex justify-between"><span className="text-zinc-400">Phone</span><span className="text-zinc-100">{brProfile.phone || "—"}</span></div>
            <div className="flex justify-between"><span className="text-zinc-400">Website</span><span className="text-cyan-300">{brProfile.website || "—"}</span></div>
            <div className="flex justify-between"><span className="text-zinc-400">Email</span><span className="text-zinc-100">{brProfile.email || "—"}</span></div>
          </div>
        </div>

        {/* Global Profile */}
        <div className="p-6 rounded-xl border border-zinc-700/60 bg-zinc-800/50" data-testid="identity-profile-global">
          <div className="flex items-center gap-2 mb-3">
            <ShieldCheck size={16} className="text-zinc-400" />
            <h3 className="text-base font-semibold text-zinc-100">Global Financial Profile</h3>
            <span className="ml-auto px-2 py-0.5 text-[10px] font-semibold rounded bg-zinc-600/40 text-zinc-300">DEFAULT</span>
          </div>
          <p className="text-xs text-zinc-400 mb-3">Applied to non-Brazil financial documents. No CNPJ by default.</p>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-zinc-400">Company</span><span className="text-zinc-100">{globalProfile.company_name || "—"}</span></div>
            <div className="flex justify-between"><span className="text-zinc-400">HQ</span><span className="text-zinc-100 text-xs">{globalProfile.hq_address || "—"}</span></div>
            <div className="flex justify-between"><span className="text-zinc-400">Brazil Office</span><span className="text-zinc-100 text-xs">{globalProfile.br_address || "—"}</span></div>
            <div className="flex justify-between"><span className="text-zinc-400">Phone</span><span className="text-zinc-100">{globalProfile.phone || "—"}</span></div>
            <div className="flex justify-between"><span className="text-zinc-400">Website</span><span className="text-cyan-300">{globalProfile.website || "—"}</span></div>
            <div className="flex justify-between"><span className="text-zinc-400">Email</span><span className="text-zinc-100">{globalProfile.email || "—"}</span></div>
          </div>
        </div>
      </div>

      {/* API Modules Grid */}
      <div className="p-6 rounded-xl border border-zinc-700/60 bg-zinc-800/50">
        <h3 className="text-lg font-semibold text-zinc-100 mb-4">Available API Modules</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {modules.map(m => (
            <div key={m} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-zinc-700/40 text-sm text-zinc-200">
              <CheckCircle2 size={14} className="text-emerald-400 shrink-0" />
              {m.replace(/_/g, " ")}
            </div>
          ))}
        </div>
      </div>

      {!enabled && (
        <div className="p-4 rounded-xl border border-amber-600/40 bg-amber-900/20 flex items-start gap-3">
          <AlertCircle size={18} className="text-amber-400 mt-0.5 shrink-0" />
          <div className="text-sm text-amber-200">
            <strong>C6Bank integration is disabled.</strong> Set <code className="bg-zinc-700/60 px-1 rounded">C6BANK_ENABLED=true</code> and
            provide your <code className="bg-zinc-700/60 px-1 rounded">C6BANK_CLIENT_ID</code> / <code className="bg-zinc-700/60 px-1 rounded">C6BANK_CLIENT_SECRET</code> in the environment to activate.
          </div>
        </div>
      )}
    </div>
  );
}

// ─── BOLETO TAB ────────────────────────────────────────
function BoletoTab({ enabled }) {
  const [boletoId, setBoletoId] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const queryBoleto = async () => {
    if (!boletoId.trim()) return;
    setLoading(true); setError(null); setResult(null);
    try {
      const data = await apiFetch(`/api/c6bank/boleto/${boletoId}`);
      setResult(data);
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  const cancelBoleto = async () => {
    if (!boletoId.trim()) return;
    setLoading(true); setError(null);
    try {
      const data = await apiFetch(`/api/c6bank/boleto/${boletoId}/cancel`, { method: "PUT" });
      setResult(data);
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  return (
    <div className="space-y-6" data-testid="payments-boleto-tab">
      <div className="p-6 rounded-xl border border-zinc-700/60 bg-zinc-800/50">
        <h3 className="text-lg font-semibold text-zinc-100 mb-4">Boleto Management</h3>
        <p className="text-sm text-zinc-400 mb-4">Issue, query, download PDF, and cancel boletos (bank slips).</p>

        <div className="flex gap-3 items-end flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs text-zinc-400 mb-1">Bank Slip ID</label>
            <input
              data-testid="boleto-id-input"
              type="text" value={boletoId} onChange={e => setBoletoId(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-700 text-zinc-100 text-sm focus:border-cyan-500 focus:outline-none"
              placeholder="Enter bank_slip_id..."
            />
          </div>
          <button data-testid="boleto-query-btn" onClick={queryBoleto} disabled={loading || !enabled}
            className="px-4 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white text-sm font-medium disabled:opacity-50 flex items-center gap-2">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />} Query
          </button>
          <button data-testid="boleto-cancel-btn" onClick={cancelBoleto} disabled={loading || !enabled}
            className="px-4 py-2 rounded-lg bg-red-600/80 hover:bg-red-500 text-white text-sm font-medium disabled:opacity-50 flex items-center gap-2">
            <XCircle size={14} /> Cancel
          </button>
          <a
            data-testid="boleto-pdf-link"
            href={boletoId ? `${API}/api/c6bank/boleto/${boletoId}/pdf` : "#"}
            target="_blank" rel="noreferrer"
            className={`px-4 py-2 rounded-lg bg-zinc-700 hover:bg-zinc-600 text-zinc-100 text-sm font-medium flex items-center gap-2 ${!boletoId || !enabled ? 'pointer-events-none opacity-50' : ''}`}>
            <FileDown size={14} /> PDF
          </a>
        </div>
      </div>

      {error && (
        <div className="p-3 rounded-lg border border-red-600/40 bg-red-900/20 text-sm text-red-300 flex items-center gap-2">
          <AlertCircle size={14} /> {error}
        </div>
      )}
      {result && (
        <div className="p-4 rounded-xl border border-zinc-700/60 bg-zinc-800/50">
          <pre className="text-xs text-zinc-300 overflow-auto max-h-[300px]">{JSON.stringify(result, null, 2)}</pre>
        </div>
      )}
    </div>
  );
}

// ─── PIX TAB ───────────────────────────────────────────
function PixTab({ enabled }) {
  const [subTab, setSubTab] = useState("cob");
  const [txid, setTxid] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const pixSubTabs = [
    { id: "cob", label: "Immediate" },
    { id: "cobv", label: "Due-date" },
    { id: "batch", label: "Batch" },
    { id: "received", label: "Received" },
  ];

  const queryCharge = async () => {
    if (!txid.trim()) return;
    setLoading(true); setError(null); setResult(null);
    try {
      const path = subTab === "received" ? `/api/c6bank/pix/received/${txid}` : `/api/c6bank/pix/${subTab}/${txid}`;
      const data = await apiFetch(path);
      setResult(data);
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  return (
    <div className="space-y-6" data-testid="payments-pix-tab">
      <div className="p-6 rounded-xl border border-zinc-700/60 bg-zinc-800/50">
        <h3 className="text-lg font-semibold text-zinc-100 mb-4">PIX Payments</h3>
        <p className="text-sm text-zinc-400 mb-4">Manage immediate charges, due-date charges, batch operations, and received PIX.</p>

        <div className="flex gap-2 mb-4 flex-wrap">
          {pixSubTabs.map(t => (
            <button key={t.id} data-testid={`pix-subtab-${t.id}`}
              onClick={() => { setSubTab(t.id); setResult(null); setError(null); }}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${subTab === t.id ? 'bg-cyan-600 text-white' : 'bg-zinc-700/60 text-zinc-300 hover:bg-zinc-600'}`}>
              {t.label}
            </button>
          ))}
        </div>

        <div className="flex gap-3 items-end flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs text-zinc-400 mb-1">{subTab === "received" ? "e2eid" : "txid"}</label>
            <input
              data-testid="pix-txid-input"
              type="text" value={txid} onChange={e => setTxid(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-700 text-zinc-100 text-sm focus:border-cyan-500 focus:outline-none"
              placeholder={subTab === "received" ? "Enter e2eid..." : "Enter txid..."}
            />
          </div>
          <button data-testid="pix-query-btn" onClick={queryCharge} disabled={loading || !enabled}
            className="px-4 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white text-sm font-medium disabled:opacity-50 flex items-center gap-2">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />} Query
          </button>
        </div>
      </div>

      {error && (
        <div className="p-3 rounded-lg border border-red-600/40 bg-red-900/20 text-sm text-red-300 flex items-center gap-2">
          <AlertCircle size={14} /> {error}
        </div>
      )}
      {result && (
        <div className="p-4 rounded-xl border border-zinc-700/60 bg-zinc-800/50">
          <pre className="text-xs text-zinc-300 overflow-auto max-h-[300px]">{JSON.stringify(result, null, 2)}</pre>
        </div>
      )}
    </div>
  );
}

// ─── CHECKOUT TAB ──────────────────────────────────────
function CheckoutTab({ enabled }) {
  const [checkoutId, setCheckoutId] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const queryCheckout = async () => {
    if (!checkoutId.trim()) return;
    setLoading(true); setError(null); setResult(null);
    try {
      const data = await apiFetch(`/api/c6bank/checkout/${checkoutId}`);
      setResult(data);
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  const cancelCheckout = async () => {
    if (!checkoutId.trim()) return;
    setLoading(true); setError(null);
    try {
      const data = await apiFetch(`/api/c6bank/checkout/${checkoutId}/cancel`, { method: "PUT" });
      setResult(data);
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  return (
    <div className="space-y-6" data-testid="payments-checkout-tab">
      <div className="p-6 rounded-xl border border-zinc-700/60 bg-zinc-800/50">
        <h3 className="text-lg font-semibold text-zinc-100 mb-4">Card Checkout</h3>
        <p className="text-sm text-zinc-400 mb-4">Create, authorize, query, and cancel credit/debit card checkouts.</p>

        <div className="flex gap-3 items-end flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs text-zinc-400 mb-1">Checkout ID</label>
            <input
              data-testid="checkout-id-input"
              type="text" value={checkoutId} onChange={e => setCheckoutId(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-700 text-zinc-100 text-sm focus:border-cyan-500 focus:outline-none"
              placeholder="Enter checkout ID..."
            />
          </div>
          <button data-testid="checkout-query-btn" onClick={queryCheckout} disabled={loading || !enabled}
            className="px-4 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white text-sm font-medium disabled:opacity-50 flex items-center gap-2">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />} Query
          </button>
          <button data-testid="checkout-cancel-btn" onClick={cancelCheckout} disabled={loading || !enabled}
            className="px-4 py-2 rounded-lg bg-red-600/80 hover:bg-red-500 text-white text-sm font-medium disabled:opacity-50 flex items-center gap-2">
            <XCircle size={14} /> Cancel
          </button>
        </div>
      </div>

      {error && (
        <div className="p-3 rounded-lg border border-red-600/40 bg-red-900/20 text-sm text-red-300 flex items-center gap-2">
          <AlertCircle size={14} /> {error}
        </div>
      )}
      {result && (
        <div className="p-4 rounded-xl border border-zinc-700/60 bg-zinc-800/50">
          <pre className="text-xs text-zinc-300 overflow-auto max-h-[300px]">{JSON.stringify(result, null, 2)}</pre>
        </div>
      )}
    </div>
  );
}

// ─── SCHEDULE PAYMENTS TAB ─────────────────────────────
function ScheduleTab({ enabled }) {
  const [groupId, setGroupId] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const queryDDA = async () => {
    setLoading(true); setError(null); setResult(null);
    try {
      const data = await apiFetch("/api/c6bank/schedule-payments/query");
      setResult(data);
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  const queryItems = async () => {
    if (!groupId.trim()) return;
    setLoading(true); setError(null); setResult(null);
    try {
      const data = await apiFetch(`/api/c6bank/schedule-payments/${groupId}/items`);
      setResult(data);
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  return (
    <div className="space-y-6" data-testid="payments-schedule-tab">
      <div className="p-6 rounded-xl border border-zinc-700/60 bg-zinc-800/50">
        <h3 className="text-lg font-semibold text-zinc-100 mb-4">Schedule Payments</h3>
        <p className="text-sm text-zinc-400 mb-4">Submit payment groups, query DDA, and manage scheduled items.</p>

        <div className="flex gap-3 items-end flex-wrap mb-4">
          <button data-testid="sp-query-dda-btn" onClick={queryDDA} disabled={loading || !enabled}
            className="px-4 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white text-sm font-medium disabled:opacity-50 flex items-center gap-2">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />} Query DDA
          </button>
        </div>

        <div className="flex gap-3 items-end flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs text-zinc-400 mb-1">Group ID</label>
            <input
              data-testid="sp-group-id-input"
              type="text" value={groupId} onChange={e => setGroupId(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-700 text-zinc-100 text-sm focus:border-cyan-500 focus:outline-none"
              placeholder="Enter group_id..."
            />
          </div>
          <button data-testid="sp-query-items-btn" onClick={queryItems} disabled={loading || !enabled}
            className="px-4 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white text-sm font-medium disabled:opacity-50 flex items-center gap-2">
            <Search size={14} /> Group Items
          </button>
        </div>
      </div>

      {error && (
        <div className="p-3 rounded-lg border border-red-600/40 bg-red-900/20 text-sm text-red-300 flex items-center gap-2">
          <AlertCircle size={14} /> {error}
        </div>
      )}
      {result && (
        <div className="p-4 rounded-xl border border-zinc-700/60 bg-zinc-800/50">
          <pre className="text-xs text-zinc-300 overflow-auto max-h-[300px]">{JSON.stringify(result, null, 2)}</pre>
        </div>
      )}
    </div>
  );
}

// ─── WEBHOOKS TAB ──────────────────────────────────────
function WebhooksTab({ enabled }) {
  const [service, setService] = useState("BANK_SLIP");
  const [result, setResult] = useState(null);
  const [webhookLogs, setWebhookLogs] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const services = ["BANK_SLIP", "CHECKOUT", "PIX"];
  const webhookUrl = `${API}/api/c6bank/incoming-webhook`;

  const queryWebhooks = async () => {
    setLoading(true); setError(null); setResult(null);
    try {
      const path = service === "PIX" ? "/api/c6bank/pix/webhooks" : `/api/c6bank/webhooks?service=${service}`;
      const data = await apiFetch(path);
      setResult(data);
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  const deleteWebhook = async () => {
    setLoading(true); setError(null);
    try {
      const path = service === "PIX" ? "/api/c6bank/pix/webhooks" : `/api/c6bank/webhooks?service=${service}`;
      const data = await apiFetch(path, { method: "DELETE" });
      setResult(data);
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  const loadLogs = async () => {
    try {
      const data = await apiFetch("/api/c6bank/webhook-log?limit=20");
      setWebhookLogs(data);
    } catch (e) { /* ignore if no logs */ }
  };

  useEffect(() => { loadLogs(); }, []);

  return (
    <div className="space-y-6" data-testid="payments-webhooks-tab">
      {/* Webhook URL Card */}
      <div className="p-4 rounded-xl border border-cyan-600/30 bg-cyan-900/10">
        <div className="flex items-center gap-2 mb-2">
          <Webhook size={16} className="text-cyan-400" />
          <span className="text-sm font-medium text-cyan-300">Incoming Webhook URL</span>
        </div>
        <p className="text-xs text-zinc-400 mb-2">Register this URL with C6Bank to receive real-time payment notifications:</p>
        <code data-testid="webhook-receiver-url" className="block px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-700 text-sm text-cyan-300 break-all select-all">
          {webhookUrl}
        </code>
      </div>

      {/* Outbound webhook management */}
      <div className="p-6 rounded-xl border border-zinc-700/60 bg-zinc-800/50">
        <h3 className="text-lg font-semibold text-zinc-100 mb-4">Webhook Configuration</h3>
        <p className="text-sm text-zinc-400 mb-4">Register, query, and delete webhooks for Boleto, Checkout, and PIX events.</p>

        <div className="flex gap-3 items-end flex-wrap">
          <div>
            <label className="block text-xs text-zinc-400 mb-1">Service</label>
            <select data-testid="webhook-service-select" value={service} onChange={e => setService(e.target.value)}
              className="px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-700 text-zinc-100 text-sm focus:border-cyan-500 focus:outline-none">
              {services.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <button data-testid="webhook-query-btn" onClick={queryWebhooks} disabled={loading || !enabled}
            className="px-4 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white text-sm font-medium disabled:opacity-50 flex items-center gap-2">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />} Query
          </button>
          <button data-testid="webhook-delete-btn" onClick={deleteWebhook} disabled={loading || !enabled}
            className="px-4 py-2 rounded-lg bg-red-600/80 hover:bg-red-500 text-white text-sm font-medium disabled:opacity-50 flex items-center gap-2">
            <XCircle size={14} /> Delete
          </button>
        </div>
      </div>

      {error && (
        <div className="p-3 rounded-lg border border-red-600/40 bg-red-900/20 text-sm text-red-300 flex items-center gap-2">
          <AlertCircle size={14} /> {error}
        </div>
      )}
      {result && (
        <div className="p-4 rounded-xl border border-zinc-700/60 bg-zinc-800/50">
          <pre className="text-xs text-zinc-300 overflow-auto max-h-[300px]">{JSON.stringify(result, null, 2)}</pre>
        </div>
      )}

      {/* Incoming Webhook Log */}
      <div className="p-6 rounded-xl border border-zinc-700/60 bg-zinc-800/50">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-zinc-100">Incoming Event Log</h3>
          <button data-testid="webhook-log-refresh" onClick={loadLogs}
            className="px-3 py-1.5 rounded-lg bg-zinc-700 hover:bg-zinc-600 text-zinc-300 text-xs flex items-center gap-1.5">
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
        {webhookLogs?.logs?.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left" data-testid="webhook-log-table">
              <thead className="text-xs text-zinc-400 border-b border-zinc-700">
                <tr>
                  <th className="px-3 py-2">Time</th>
                  <th className="px-3 py-2">Service</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Reference</th>
                  <th className="px-3 py-2">Amount</th>
                  <th className="px-3 py-2">Processed</th>
                </tr>
              </thead>
              <tbody>
                {webhookLogs.logs.map((log, i) => (
                  <tr key={i} className="border-b border-zinc-700/40 hover:bg-zinc-700/20">
                    <td className="px-3 py-2 text-zinc-300 text-xs whitespace-nowrap">{new Date(log.received_at).toLocaleString()}</td>
                    <td className="px-3 py-2"><StatusBadge status={log.service} /></td>
                    <td className="px-3 py-2"><StatusBadge status={log.status?.toLowerCase()} /></td>
                    <td className="px-3 py-2 text-zinc-300 text-xs font-mono max-w-[180px] truncate">{log.reference_id || "—"}</td>
                    <td className="px-3 py-2 text-zinc-200 text-xs">{log.amount ? `R$ ${log.amount}` : "—"}</td>
                    <td className="px-3 py-2">{log.processed ? <CheckCircle2 size={14} className="text-emerald-400" /> : <Loader2 size={14} className="text-amber-400" />}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-zinc-500 italic">No incoming webhook events recorded yet.</p>
        )}
      </div>
    </div>
  );
}

// ─── STATEMENTS TAB ────────────────────────────────────
function StatementsTab({ enabled }) {
  const [stmtType, setStmtType] = useState("transactions");
  const [startDate, setStartDate] = useState(new Date().toISOString().split("T")[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split("T")[0]);
  const [page, setPage] = useState(1);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const queryStatements = async () => {
    setLoading(true); setError(null); setResult(null);
    try {
      let path;
      if (stmtType === "extrato") {
        path = `/api/c6bank/extrato?start_date=${startDate}&end_date=${endDate}`;
      } else {
        path = `/api/c6bank/statements/${stmtType}?start_date=${startDate}&page=${page}`;
      }
      const data = await apiFetch(path);
      setResult(data);
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  return (
    <div className="space-y-6" data-testid="payments-statements-tab">
      <div className="p-6 rounded-xl border border-zinc-700/60 bg-zinc-800/50">
        <h3 className="text-lg font-semibold text-zinc-100 mb-4">C6Pay Statements & Extrato</h3>
        <p className="text-sm text-zinc-400 mb-4">Query transactions, receivables, and account statements (extrato).</p>

        <div className="flex gap-3 items-end flex-wrap">
          <div>
            <label className="block text-xs text-zinc-400 mb-1">Type</label>
            <select data-testid="stmt-type-select" value={stmtType} onChange={e => setStmtType(e.target.value)}
              className="px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-700 text-zinc-100 text-sm focus:border-cyan-500 focus:outline-none">
              <option value="transactions">Transactions</option>
              <option value="receivables">Receivables</option>
              <option value="extrato">Extrato (Account Statement)</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-zinc-400 mb-1">Start Date</label>
            <input data-testid="stmt-date-input" type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
              className="px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-700 text-zinc-100 text-sm focus:border-cyan-500 focus:outline-none" />
          </div>
          {stmtType === "extrato" ? (
            <div>
              <label className="block text-xs text-zinc-400 mb-1">End Date (max 30d range)</label>
              <input data-testid="stmt-end-date-input" type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
                className="px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-700 text-zinc-100 text-sm focus:border-cyan-500 focus:outline-none" />
            </div>
          ) : (
            <div>
              <label className="block text-xs text-zinc-400 mb-1">Page</label>
              <input data-testid="stmt-page-input" type="number" min="1" value={page} onChange={e => setPage(parseInt(e.target.value) || 1)}
                className="w-20 px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-700 text-zinc-100 text-sm focus:border-cyan-500 focus:outline-none" />
            </div>
          )}
          <button data-testid="stmt-query-btn" onClick={queryStatements} disabled={loading || !enabled}
            className="px-4 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white text-sm font-medium disabled:opacity-50 flex items-center gap-2">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />} Query
          </button>
        </div>
      </div>

      {error && (
        <div className="p-3 rounded-lg border border-red-600/40 bg-red-900/20 text-sm text-red-300 flex items-center gap-2">
          <AlertCircle size={14} /> {error}
        </div>
      )}
      {result && (
        <div className="p-4 rounded-xl border border-zinc-700/60 bg-zinc-800/50">
          <pre className="text-xs text-zinc-300 overflow-auto max-h-[300px]">{JSON.stringify(result, null, 2)}</pre>
        </div>
      )}
    </div>
  );
}

// ─── MAIN PAGE ─────────────────────────────────────────
export default function PaymentsPage() {
  const { t } = useTranslation(["common"]);
  const [activeTab, setActiveTab] = useState("overview");
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiFetch("/api/c6bank/status")
      .then(setStatus)
      .catch(() => setStatus({ enabled: false, environment: "unknown", has_credentials: false, compliance: {}, api_modules: [] }))
      .finally(() => setLoading(false));
  }, []);

  const enabled = status?.enabled || false;

  if (loading) {
    return (
      <QMSLayout title="Payments">
        <div className="flex items-center justify-center h-64">
          <Loader2 size={32} className="animate-spin text-cyan-400" />
        </div>
      </QMSLayout>
    );
  }

  return (
    <QMSLayout title="C6Bank Payments">
      <div className="max-w-[1200px] mx-auto" data-testid="payments-page">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-zinc-100">C6Bank Payments</h1>
          <p className="text-sm text-zinc-400 mt-1">
            Financial document identity governed by region-aware profiles
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 overflow-x-auto pb-1 border-b border-zinc-700/60" data-testid="payments-tabs">
          {TABS.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                data-testid={`payments-tab-${tab.id}`}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium whitespace-nowrap rounded-t-lg transition-colors
                  ${activeTab === tab.id
                    ? "bg-zinc-800 text-cyan-400 border-b-2 border-cyan-400"
                    : "text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800/50"}`}
              >
                <Icon size={16} /> {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        {activeTab === "overview" && <OverviewTab status={status} />}
        {activeTab === "boleto" && <BoletoTab enabled={enabled} />}
        {activeTab === "pix" && <PixTab enabled={enabled} />}
        {activeTab === "checkout" && <CheckoutTab enabled={enabled} />}
        {activeTab === "schedule" && <ScheduleTab enabled={enabled} />}
        {activeTab === "webhooks" && <WebhooksTab enabled={enabled} />}
        {activeTab === "statements" && <StatementsTab enabled={enabled} />}
      </div>
    </QMSLayout>
  );
}
